import React, { Dispatch, useContext, useReducer } from "react";

import { Roles } from "../models/autogenerated-deliveryService-gql/graphql";
import { User } from "../models/user";
import { IUserAction, nullUser, userReducer } from "./reducers/userReducer";

//#region create Context
interface UserContextObj {
  user: User;
  dispatchUser: Dispatch<IUserAction>;
  isCustomer: () => boolean;
  hasOwner: (owner: string) => boolean;
  getOwnerForRequests: () => string | undefined;
  getAllOwners: () => string[];
}

const UserContext = React.createContext<UserContextObj | null>(null);
//#endregion

//#region useContext Hook
export const useUserContext = () => {
  const context = useContext(UserContext);

  if (!context)
    throw new Error("UserContext must be placed within UserProvider");

  return context;
};
//#endregion

type UserContextProviderProps = {
  children: React.ReactNode;
};

const UserContextProvider = (props: UserContextProviderProps) => {
  const { children } = props;

  const [user, dispatchUser] = useReducer(userReducer, nullUser);

  const isCustomer = () =>
    user.isCustomer ||
    user.role === Roles.Customer ||
    user.role === Roles.SupplierAndCustomer;

  const hasOwner = (owner: string) =>
    user.owners.some(
      (candidate) =>
        candidate.trim().toLowerCase() === owner.trim().toLowerCase(),
    );

  const getOwnerForRequests = () => {
    if (user.owners.length === 1) return user.owners[0];
    return undefined;
  };

  const getAllOwners = () => user.owners;

  const value = {
    user,
    dispatchUser,
    isCustomer,
    hasOwner,
    getOwnerForRequests,
    getAllOwners,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export { UserContext, UserContextProvider };
